import React, {useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSignOutAlt, faMoon, faSun, faGear, faUserShield} from '@fortawesome/free-solid-svg-icons';
import {useAuth} from '../../contexts/AuthContext';
import {useTheme} from '../../contexts/ThemeContext';
import {ROLE_LABELS} from '../../utils/roles';
import {useClickOutside} from '../../hooks/useClickOutside';
import {TenantAvatar} from '../UI/TenantAvatar';

export const UserMenu: React.FC = () => {
    const navigate = useNavigate();
    const {user, role, canManage, logout} = useAuth();
    const {isDarkMode, toggleDarkMode} = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useClickOutside(containerRef, isOpen, () => setIsOpen(false));

    const MenuItem: React.FC<{
        icon: typeof faGear;
        label: string;
        onClick: () => void;
        tone?: string;
    }> = ({icon, label, onClick, tone = 'text-slate-600 dark:text-slate-300'}) => (
        <button
            onClick={() => {
                onClick();
                setIsOpen(false);
            }}
            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-left
                hover:bg-white/60 dark:hover:bg-white/10 transition-colors ${tone}`}
        >
            <FontAwesomeIcon icon={icon} className="w-4 flex-shrink-0"/>
            {label}
        </button>
    );

    return (
        <div ref={containerRef} className="relative">
            <button
                onClick={() => setIsOpen(open => !open)}
                className="flex items-center gap-2 p-1 pr-2 rounded-full hover:bg-white/60 dark:hover:bg-white/10 transition-colors"
                aria-label="Tài khoản"
                aria-expanded={isOpen}
            >
                <TenantAvatar name={user?.displayName ?? undefined} email={user?.email ?? undefined} size="sm"/>
                <span className="hidden xl:block max-w-[8rem] truncate text-sm font-medium text-slate-700 dark:text-slate-200">
                    {user?.displayName || user?.email}
                </span>
            </button>

            {isOpen && (
                <div className="absolute right-0 top-full mt-2 z-50 w-64 glass-panel overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/40 dark:border-white/10">
                        <p className="text-sm font-semibold text-slate-800 dark:text-white truncate">
                            {user?.displayName || 'Tài khoản'}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email}</p>
                        <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold
                            bg-slate-500/15 text-slate-600 dark:text-slate-300">
                            {role ? ROLE_LABELS[role] : 'Chưa cấp quyền'}
                        </span>
                    </div>

                    <div className="py-1">
                        <MenuItem
                            icon={isDarkMode ? faSun : faMoon}
                            label={isDarkMode ? 'Chế độ sáng' : 'Chế độ tối'}
                            onClick={toggleDarkMode}
                        />
                        {/* Cùng bộ với navItems.ts: Quản lý vào được cả hai trang
                            (duyệt người thuê, sửa giá mặc định) - phần chỉ dành cho
                            Admin thì hai trang đó tự ẩn bên trong. */}
                        {canManage && (
                            <>
                                <MenuItem icon={faUserShield} label="Quản lý người dùng" onClick={() => navigate('/phanquyen')}/>
                                <MenuItem icon={faGear} label="Cài đặt hệ thống" onClick={() => navigate('/caidat')}/>
                            </>
                        )}
                    </div>

                    <div className="py-1 border-t border-white/40 dark:border-white/10">
                        <MenuItem
                            icon={faSignOutAlt}
                            label="Đăng xuất"
                            onClick={logout}
                            tone="text-rose-600 dark:text-rose-400"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
